import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Users,
  Plane,
  Award,
  Globe,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import AgentMeetingSection from "../components/AgentMeetingSection";
import AgentPricingSection from "../components/AgentPricingSection";
import WorkProcessSection from "../components/WorkProcessSection";
import { HomeWhatsAppFloat } from "../components/HomeWhatsAppFloat";

const WHATSAPP_NUMBER = "19544776420";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hola, soy agente de la red Divídelo y quiero coordinar un caso."
)}`;

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-navy-50 font-body">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-b border-navy-100 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-11 h-11 bg-gradient-to-br from-red-600 to-blue-700 rounded-lg flex items-center justify-center text-white font-bold shadow-md">
              D
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading text-lg font-black text-blue-900">Divídelo</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-red-600 font-black" data-testid="landing-logo-slogan">
                Visa Aprobada
              </span>
            </div>
            <span className="inline-block text-[9px] sm:text-[10px] uppercase tracking-[0.14em] text-navy-500 font-bold ml-2 border-l border-navy-200 pl-2 sm:pl-3 max-w-[140px] sm:max-w-[220px] leading-tight">
              La más grande red de agentes migratorios de Latinoamérica
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <a href="#nosotros" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Nosotros
            </a>
            <a href="#metodo" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Método
            </a>
            <a href="#proceso" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Proceso
            </a>
            <a href="#trabajamos-asi" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Cómo trabajamos
            </a>
            <a href="#servicios" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Servicios
            </a>
            <a href="#precios" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Precios
            </a>
            <a href="#formulario" className="text-sm font-medium text-blue-900 hover:text-red-600">
              Agendar cita
            </a>
            <a href="#quiero-ser-agente" className="text-sm font-medium text-red-600 hover:text-red-700 font-black" data-testid="landing-nav-become-agent">
              Quiero ser agente
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 relative" data-testid="landing-hero">
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold mb-6"
          >
            <ShieldCheck className="w-4 h-4" />
            Plataforma exclusiva para agentes de la red Divídelo
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-blue-950 leading-tight mb-6"
          >
            Asesoría migratoria{" "}
            <span className="text-red-600">para clientes de nuestros agentes</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-navy-700 text-lg sm:text-xl max-w-3xl mx-auto mb-8 leading-relaxed"
          >
            Somos una red cerrada de asesoramiento para visas de <strong className="text-blue-900">Turismo</strong> y{" "}
            <strong className="text-blue-900">Negocio</strong> hacia <strong className="text-red-600">Estados Unidos</strong>, Canadá y México.
            <br />
            <span className="text-navy-500">
              Solo atendemos casos referidos por agentes acreditados de Divídelo.
            </span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a href="#formulario" data-testid="hero-cta-form">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 shadow-lg">
                Agendar cita Zoom <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 shadow-lg">
                <MessageCircle className="w-5 h-5 mr-2" /> Contactar por WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Value pillars */}
      <section id="nosotros" className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center mb-14" data-testid="landing-about-block">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs uppercase tracking-widest font-semibold mb-4">
            ¿Qué es Divídelo?
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Somos la plataforma detrás de miles de <span className="text-emerald-600">visas aprobadas</span>
          </h2>
          <div className="text-navy-600 leading-relaxed space-y-4 text-lg">
            <p>
              Divídelo nació para <strong>simplificar el proceso de visa</strong> hacia Estados Unidos,
              Canadá y México. Combinamos <strong>tecnología, inteligencia artificial y asesores humanos</strong> con
              experiencia consular real para preparar a cada solicitante como si fuera el único.
            </p>
            <p>
              Ayudamos a nuestros agentes a llevar a sus clientes por un proceso claro: entrevista
              simulada, DS-160 revisado, adelanto de cita consular y acompañamiento continuo — todo
              coordinado por un asesor asignado.
            </p>
            <p className="text-navy-500 text-base italic">
              Hoy operamos exclusivamente a través de nuestra red de agentes acreditados para
              garantizar calidad, seguimiento y resultados.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              Trabajamos <span className="text-emerald-600">exclusivamente</span> con agentes acreditados
            </h2>
            <p className="text-navy-600 max-w-2xl mx-auto">
              Un modelo profesional, transparente y centrado en el éxito del cliente final.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Red cerrada",
                desc: "Sólo procesamos casos que llegan a través de nuestros agentes acreditados. No atendemos público general.",
                color: "emerald",
              },
              {
                icon: Award,
                title: "Asesores expertos",
                desc: "Cada caso lo revisa un asesor con experiencia comprobable en trámites consulares y adelanto de cita.",
                color: "blue",
              },
              {
                icon: Users,
                title: "Familia primero",
                desc: "Damos prioridad y beneficios especiales a grupos familiares. Los casos individuales tienen tarifa fija.",
                color: "amber",
              },
              {
                icon: Globe,
                title: "3 destinos principales",
                desc: "Especialistas en Estados Unidos, Canadá y México — visas de Turismo (B1/B2) y Negocio.",
                color: "violet",
              },
              {
                icon: Plane,
                title: "Adelanto de cita",
                desc: "Programa insignia con promedio de 300 días calendario de adelanto en citas consulares.",
                color: "rose",
              },
              {
                icon: MessageCircle,
                title: "Todo por WhatsApp",
                desc: "Cada venta y cada seguimiento se coordina por WhatsApp con el agente responsable.",
                color: "teal",
              },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-navy-50 border border-navy-100 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                  data-testid={`landing-value-card-${i}`}
                >
                  <div className={`w-11 h-11 rounded-xl bg-${f.color}-100 flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 text-${f.color}-600`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-navy-900 mb-2">
                    {f.title}
                  </h3>
                  <p className="text-navy-600 text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="proceso" className="py-16 px-6 bg-white" data-testid="landing-process">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs uppercase tracking-widest font-semibold mb-4">
            Cómo funciona
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-3">
            Un proceso claro para tu cliente y para ti
          </h2>
          <p className="text-navy-600 max-w-2xl mx-auto">
            Desde el envío del caso hasta la aprobación de la visa, siempre sabrás en qué paso está.
          </p>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-6">
          {[
            {
              step: "1",
              title: "Envías el caso",
              desc: "Completas el formulario con los datos del cliente y su historial migratorio.",
            },
            {
              step: "2",
              title: "Revisión experta",
              desc: "Un asesor especializado analiza el perfil y te contacta por WhatsApp con la estrategia.",
            },
            {
              step: "3",
              title: "Preparación completa",
              desc: "DS-160, documentos, simulador con IA y adelanto de cita si aplica.",
            },
            {
              step: "4",
              title: "Entrevista aprobada",
              desc: "Acompañamos hasta la cita consular y celebramos la aprobación con tu cliente.",
            },
          ].map((s) => (
            <div
              key={s.step}
              className="relative bg-navy-50 border border-navy-100 rounded-2xl p-6"
              data-testid={`landing-process-step-${s.step}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center mb-4">
                {s.step}
              </div>
              <h4 className="font-heading text-lg font-bold text-navy-900 mb-1.5">{s.title}</h4>
              <p className="text-navy-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Work Process (Fase 0 + países) */}
      <WorkProcessSection />

      {/* Method / 95% Success Section */}
      <section id="metodo" className="py-16 px-6" data-testid="landing-method">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs uppercase tracking-widest font-semibold mb-4">
              Nuestro método
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              Ex cónsules calificados + IA de última generación
            </h2>
            <p className="text-navy-600 text-lg max-w-3xl mx-auto">
              Nuestro sistema de aprobación combina la experiencia real de <strong>ex cónsules</strong> con la <strong>inteligencia artificial más avanzada</strong> del mercado migratorio.
            </p>
          </div>

          {/* 95% highlight */}
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900 via-blue-800 to-red-700 text-white rounded-3xl p-8 sm:p-12 shadow-2xl mb-12 relative overflow-hidden" data-testid="landing-method-95">
            {/* Stars background pattern */}
            <div className="absolute top-4 right-4 text-white/20 text-6xl leading-none select-none">★ ★ ★</div>
            <div className="absolute bottom-4 left-4 text-white/10 text-8xl leading-none select-none">★</div>
            <div className="absolute top-1/2 left-1/3 text-white/5 text-9xl leading-none select-none">★</div>
            <div className="flex flex-col md:flex-row items-center gap-8 relative">
              <div className="text-center md:text-left">
                <p className="text-red-200 text-sm uppercase tracking-widest font-black mb-2 flex items-center gap-2 justify-center md:justify-start">
                  <span>★</span> Índice de éxito <span>★</span>
                </p>
                <p className="font-heading text-7xl sm:text-8xl font-black leading-none drop-shadow-lg">
                  95%
                </p>
                <p className="text-red-100 text-sm mt-2 font-semibold">
                  de seguridad en tu aprobación
                </p>
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-black mb-3">
                  Un sistema diseñado para aprobarte
                </h3>
                <p className="text-blue-50 leading-relaxed">
                  Analizamos cada caso con la misma rigurosidad que un cónsul revisa una entrevista real. La IA identifica riesgos, refuerza fortalezas y prepara al solicitante para responder cualquier pregunta con seguridad.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-red-200 rounded-2xl p-6 shadow-lg" data-testid="landing-method-consuls">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-red-600" />
              </div>
              <h4 className="font-heading text-xl font-black text-blue-900 mb-2">
                Ex cónsules calificados
              </h4>
              <p className="text-navy-600 text-sm leading-relaxed">
                Profesionales con experiencia real en oficinas consulares y trámites internacionales revisan cada perfil. Saben exactamente qué buscan los cónsules porque estuvieron del otro lado del cristal.
              </p>
            </div>
            <div className="bg-white border-2 border-blue-200 rounded-2xl p-6 shadow-lg" data-testid="landing-method-ai">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-blue-700" />
              </div>
              <h4 className="font-heading text-xl font-black text-blue-900 mb-2">
                IA más avanzada del mercado
              </h4>
              <p className="text-navy-600 text-sm leading-relaxed">
                Simuladores de entrevista con IA, análisis predictivo del perfil, detección automática de riesgos y recomendaciones personalizadas para cada solicitante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="servicios" className="py-16 px-6">
        <div className="max-w-5xl mx-auto text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
            ¿Qué gestionamos?
          </h2>
          <p className="text-navy-600 max-w-2xl mx-auto">
            Todos los tipos de acompañamiento migratorio para tus clientes.
          </p>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-navy-100" data-testid="landing-service-visa">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-navy-900">Visa Turismo / Negocio</h3>
            </div>
            <ul className="space-y-2 text-navy-700 text-sm">
              <li>• Preparación completa del DS-160</li>
              <li>• Simulación de entrevista consular con IA</li>
              <li>• Revisión ilimitada de documentos</li>
              <li>• Estrategia según perfil migratorio</li>
              <li>• Estados Unidos · Canadá · México</li>
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-navy-100" data-testid="landing-service-adelanto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                <Plane className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-navy-900">Adelanto de Cita</h3>
            </div>
            <ul className="space-y-2 text-navy-700 text-sm">
              <li>• Promedio 300 días calendario de adelanto</li>
              <li>• Gestión directa con consulados</li>
              <li>• Alertas y monitoreo continuo</li>
              <li>• Servicio independiente o incluido en planes</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <AgentPricingSection />

      {/* Zoom booking with agent */}
      <AgentMeetingSection />

      {/* Quiero ser Agente */}
      <section id="quiero-ser-agente" className="py-16 px-6 bg-gradient-to-br from-blue-900 via-blue-950 to-red-900 text-white relative overflow-hidden" data-testid="landing-become-agent">
        {/* Stars pattern */}
        <div className="absolute top-8 left-10 text-white/10 text-6xl select-none">★</div>
        <div className="absolute top-24 left-32 text-white/15 text-4xl select-none">★</div>
        <div className="absolute top-16 right-12 text-white/10 text-7xl select-none">★</div>
        <div className="absolute bottom-20 left-1/4 text-white/10 text-5xl select-none">★</div>
        <div className="absolute bottom-32 right-1/3 text-white/15 text-4xl select-none">★</div>
        <div className="absolute top-1/2 right-20 text-white/10 text-6xl select-none">★</div>

        <div className="max-w-4xl mx-auto text-center relative">
          <span className="inline-block px-3 py-1 rounded-full bg-red-500/30 text-red-100 text-xs uppercase tracking-widest font-black mb-4 border border-red-400/40">
            ★ ¿No eres agente todavía? ★
          </span>
          <h2 className="font-heading text-3xl sm:text-5xl font-black mb-4">
            Quiero ser <span className="text-red-300">agente Divídelo</span>
          </h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
            Únete a nuestra red de agentes acreditados a través de <strong className="text-white">Club Viajemos</strong>.
            Cada semana realizamos eventos en vivo donde conocerás el modelo, los beneficios y cómo empezar a
            enviar casos.
          </p>

          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
            <p className="text-sm uppercase tracking-widest text-red-300 font-black mb-4 flex items-center gap-2 justify-center">
              <span>★</span> Eventos en vivo cada semana <span>★</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {["Lunes", "Miércoles", "Sábado"].map((day) => (
                <div
                  key={day}
                  className="bg-white/10 border-2 border-red-400/40 rounded-xl p-5 hover:border-red-300 transition-all"
                  data-testid={`event-day-${day.toLowerCase()}`}
                >
                  <p className="text-red-300 text-3xl mb-1">★</p>
                  <p className="font-heading text-xl font-black">{day}</p>
                  <p className="text-blue-200 text-xs mt-1">Evento en vivo</p>
                </div>
              ))}
            </div>
          </div>

          <a
            href="https://www.clubviajemos.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="become-agent-cta"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black px-8 py-6 shadow-2xl border-2 border-white/20"
            >
              ★ Ir a Club Viajemos ★ <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>

          <p className="text-blue-200 text-sm mt-4">
            Entra a <span className="text-white font-semibold">www.clubviajemos.com</span> y elige el evento de la semana que más te acomode.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-950 text-white py-12 px-6" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-red-600 to-blue-700 rounded-lg flex items-center justify-center text-white font-bold">
              D
            </div>
            <div className="flex flex-col leading-tight">
              <p className="font-heading font-bold text-white">Divídelo</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-red-400 font-bold">
                ★ Visa Aprobada ★
              </p>
              <p className="text-navy-400 text-xs mt-0.5 max-w-[240px]">La más grande red de agentes migratorios de Latinoamérica</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terminos" className="text-navy-400 hover:text-white">
              Términos
            </Link>
            <Link to="/privacidad" className="text-navy-400 hover:text-white">
              Privacidad
            </Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-navy-400 hover:text-white">
              +1 954 477 6420
            </a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-navy-800 text-center text-xs text-navy-500">
          © 2026 Divídelo · Todos los derechos reservados
        </div>
      </footer>
      <HomeWhatsAppFloat />
    </div>
  );
};

// Send icon shim (imported above via lucide but only if we use it inline)

export default LandingPage;
